<template>
  <div class="diagnostics-page" style="padding-top: 0">
    <div class="menu-title" style="padding-left: 0">日志诊断报告</div>
    <div class="menu-content">
      <el-card shadow="never" class="filter-card">
        <el-form :inline="true" size="small" @submit.native.prevent="loadReports">
          <el-form-item label="报告编号">
            <el-input v-model.trim="filters.report_id" clearable placeholder="rpt_..." />
          </el-form-item>
          <el-form-item label="设备标识">
            <el-input v-model.trim="filters.device_ref" clearable placeholder="dev_..." />
          </el-form-item>
          <el-form-item label="trace_id">
            <el-input v-model.trim="filters.trace_id" clearable />
          </el-form-item>
          <el-form-item label="task_id">
            <el-input v-model.trim="filters.task_id" clearable />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="filters.status" clearable placeholder="全部">
              <el-option label="打开" value="OPEN" />
              <el-option label="处理中" value="INVESTIGATING" />
              <el-option label="已解决" value="RESOLVED" />
              <el-option label="已关闭" value="CLOSED" />
            </el-select>
          </el-form-item>
          <el-form-item label="报错时间">
            <el-date-picker
              v-model="timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :default-time="['00:00:00', '23:59:59']"
              clearable
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="loading" @click="loadReports">查询</el-button>
            <el-button @click="resetFilters">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card shadow="never" class="table-card">
        <el-table v-loading="loading" :data="reports" border stripe height="520">
          <el-table-column prop="report_id" label="报告编号" min-width="170" show-overflow-tooltip />
          <el-table-column prop="device_ref" label="设备标识" min-width="150" show-overflow-tooltip />
          <el-table-column prop="feature" label="功能" min-width="110" />
          <el-table-column prop="error_code" label="错误码" min-width="130" />
          <el-table-column prop="primary_trace_id" label="trace_id" min-width="170" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="110">
            <template slot-scope="scope">
              <el-tag size="mini" :type="statusTag(scope.row.status)">{{ statusLabel(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="发生时间" min-width="180">
            <template slot-scope="scope">{{ formatLocalTime(scope.row.occurred_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" @click="openDetail(scope.row)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination"
          background
          layout="total, prev, pager, next, sizes"
          :current-page="page"
          :page-size="size"
          :page-sizes="[20, 50, 100]"
          :total="total"
          @current-change="changePage"
          @size-change="changeSize"
        />
      </el-card>
    </div>

    <el-dialog title="诊断报告详情" :visible.sync="detailVisible" width="900px" top="5vh">
      <div v-if="detail" class="detail-body">
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="报告编号">{{ detail.report_id }}</el-descriptions-item>
          <el-descriptions-item label="设备标识">{{ detail.device_ref }}</el-descriptions-item>
          <el-descriptions-item label="安装标识">{{ detail.installation_ref || '-' }}</el-descriptions-item>
          <el-descriptions-item label="功能">{{ detail.feature || '-' }}</el-descriptions-item>
          <el-descriptions-item label="错误码">{{ detail.error_code || '-' }}</el-descriptions-item>
          <el-descriptions-item label="指纹">{{ detail.error_fingerprint || '-' }}</el-descriptions-item>
          <el-descriptions-item label="trace_id">{{ detail.primary_trace_id || '-' }}</el-descriptions-item>
          <el-descriptions-item label="task_id">{{ detail.primary_task_id || '-' }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-select v-model="detail.status" size="mini" @change="saveStatus">
              <el-option label="打开" value="OPEN" />
              <el-option label="处理中" value="INVESTIGATING" />
              <el-option label="已解决" value="RESOLVED" />
              <el-option label="已关闭" value="CLOSED" />
            </el-select>
          </el-descriptions-item>
          <el-descriptions-item label="发生时间">{{ formatLocalTime(detail.occurred_at) }}</el-descriptions-item>
          <el-descriptions-item label="采集方式">{{ collectionModeLabel(detail.collection_mode) }}</el-descriptions-item>
          <el-descriptions-item label="日志来源">{{ diagnosticSourcesLabel(detail.consent_scope) }}</el-descriptions-item>
          <el-descriptions-item label="日志范围" :span="3">
            {{ formatLocalTime(detail.range_start) }} 至 {{ formatLocalTime(detail.range_end) }}
          </el-descriptions-item>
          <el-descriptions-item label="问题描述" :span="3">{{ detail.summary_redacted || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div class="detail-actions">
          <el-button size="small" type="primary" :disabled="!openObserveUrl" @click="openOpenObserve">在 OpenObserve 查看</el-button>
          <el-button size="small" @click="copyQuery">复制查询条件</el-button>
          <el-button size="small" :disabled="detail.bundle_status !== 'COMPLETED'" @click="downloadVisible = true">下载诊断包</el-button>
        </div>
        <p v-if="queryText" class="query-text">{{ queryText }}</p>

        <h4>调用链时间线（{{ events.length }}）</h4>
        <el-timeline v-if="events.length">
          <el-timeline-item v-for="item in events" :key="item.event_uid || item.id" :timestamp="formatLocalTime(item.occurred_at || item.received_at)" :type="timelineType(item.level)">
            <strong>{{ item.event_name || item.event || item.component || 'event' }}</strong>
            <span class="event-meta">{{ item.source_service || item.service }} · {{ item.endpoint || '-' }} · {{ item.level }}</span>
            <p>{{ item.message_redacted || item.message || '' }}</p>
            <code v-if="item.trace_id">trace={{ item.trace_id }}</code>
            <code v-if="item.task_id"> task={{ item.task_id }}</code>
            <code v-if="item.request_id || item.bridge_request_id"> request={{ item.request_id || item.bridge_request_id }}</code>
          </el-timeline-item>
        </el-timeline>
        <el-empty v-else description="暂无结构化事件，点击 OpenObserve 查看服务端日志" />
      </div>
    </el-dialog>

    <el-dialog title="下载诊断包" :visible.sync="downloadVisible" width="430px">
      <el-input v-model.trim="downloadReason" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请填写下载原因，操作会记录审计日志" />
      <span slot="footer">
        <el-button @click="downloadVisible = false">取消</el-button>
        <el-button type="primary" :loading="downloading" @click="downloadBundle">确认下载</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import { getDiagnosticReport, listDiagnosticReports, updateDiagnosticReport, downloadDiagnosticBundle } from '@/api/diagnostics'

const LOCAL_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
})

function safeHttpUrl(value) {
  try {
    const raw = String(value || '').trim()
    if (!raw) return ''
    const parsed = new URL(raw, window.location.origin)
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : ''
  } catch (error) {
    return ''
  }
}

function formatBrowserLocalTime(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return LOCAL_DATE_TIME_FORMATTER.format(parsed)
}

function withOpenObserveRange(value, rangeStart, rangeEnd) {
  const safeUrl = safeHttpUrl(value)
  if (!safeUrl) return ''
  const fromMillis = Date.parse(String(rangeStart || ''))
  const toMillis = Date.parse(String(rangeEnd || ''))
  if (!Number.isFinite(fromMillis) || !Number.isFinite(toMillis) || fromMillis > toMillis) {
    return safeUrl
  }
  const parsed = new URL(safeUrl)
  // OpenObserve v0.91 web log links use Unix timestamps in microseconds.
  parsed.searchParams.set('from', String(Math.trunc(fromMillis * 1000)))
  parsed.searchParams.set('to', String(Math.trunc(toMillis * 1000)))
  return parsed.href
}

function copyTextFallback(value) {
  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    return typeof document.execCommand === 'function' && document.execCommand('copy')
  } catch (error) {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

export default {
  name: 'DiagnosticReports',
  data() {
    return {
      loading: false,
      reports: [],
      total: 0,
      page: 1,
      size: 20,
      filters: { report_id: '', device_ref: '', trace_id: '', task_id: '', status: '' },
      timeRange: [],
      detailVisible: false,
      detail: null,
      events: [],
      queryText: '',
      openObserveUrl: '',
      downloadVisible: false,
      downloadReason: '',
      downloading: false
    }
  },
  mounted() { this.loadReports() },
  methods: {
    async loadReports() {
      this.loading = true
      try {
        const [from, to] = Array.isArray(this.timeRange) ? this.timeRange : []
        const result = await listDiagnosticReports({
          ...this.filters,
          from: from ? new Date(from).toISOString() : '',
          to: to ? new Date(to).toISOString() : '',
          page: this.page,
          size: this.size
        })
        this.reports = result?.records || result?.list || result?.items || []
        this.total = Number(result?.total || result?.totalCount || this.reports.length)
      } catch (error) {
        Message.error(error?.message || '读取诊断报告失败')
      } finally { this.loading = false }
    },
    resetFilters() { this.filters = { report_id: '', device_ref: '', trace_id: '', task_id: '', status: '' }; this.timeRange = []; this.page = 1; this.loadReports() },
    changePage(page) { this.page = page; this.loadReports() },
    changeSize(size) { this.size = size; this.page = 1; this.loadReports() },
    async openDetail(row) {
      try {
        const result = await getDiagnosticReport(row.report_id || row.reportNo || row.id)
        this.detail = result?.report || result
        this.events = result?.events || this.detail?.events || []
        this.queryText = result?.openobserve_query || this.detail?.openobserve_query || ''
        this.openObserveUrl = withOpenObserveRange(
          result?.openobserve_search_url || this.detail?.openobserve_search_url,
          this.detail?.range_start,
          this.detail?.range_end
        )
        this.detailVisible = true
      } catch (error) { Message.error(error?.message || '读取报告详情失败') }
    },
    async saveStatus(status) {
      try { await updateDiagnosticReport(this.detail.report_id, { status }); Message.success('状态已更新'); this.loadReports() } catch (error) { Message.error(error?.message || '更新状态失败') }
    },
    statusLabel(status) { return ({ OPEN: '打开', INVESTIGATING: '处理中', RESOLVED: '已解决', CLOSED: '已关闭' }[status] || status || '-') },
    statusTag(status) { return status === 'RESOLVED' ? 'success' : status === 'CLOSED' ? 'info' : status === 'INVESTIGATING' ? 'warning' : 'danger' },
    timelineType(level) { return ['error', 'critical', 'ERROR', 'CRITICAL'].includes(level) ? 'danger' : level === 'warning' || level === 'WARN' ? 'warning' : 'primary' },
    collectionModeLabel(mode) { return mode === 'automatic' ? '自动采集' : '用户确认' },
    diagnosticSourcesLabel(scope) {
      const labels = {
        events: '结构化事件',
        core_logs: 'Core 日志',
        electron_logs: 'Electron 日志',
        updater_logs: '更新日志'
      }
      const sources = Array.isArray(scope?.included_sources) ? scope.included_sources : []
      return sources.map(source => labels[source] || source).join('、') || '-'
    },
    formatLocalTime(value) { return formatBrowserLocalTime(value) },
    async copyQuery() {
      if (!this.queryText) {
        Message.warning('暂无可复制的查询条件')
        return
      }
      let copied = false
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(this.queryText)
          copied = true
        } catch (error) {
          copied = false
        }
      }
      if (!copied) copied = copyTextFallback(this.queryText)
      if (copied) {
        Message.success('查询条件已复制')
      } else {
        Message.error('浏览器无法访问剪贴板，请手动复制查询条件')
      }
    },
    openOpenObserve() { if (this.openObserveUrl) window.open(this.openObserveUrl, '_blank', 'noopener,noreferrer') },
    async downloadBundle() {
      if (!this.downloadReason) { Message.warning('请填写下载原因'); return }
      this.downloading = true
      try {
        const response = await downloadDiagnosticBundle(this.detail.report_id, this.downloadReason)
        const blob = new Blob([response.data], { type: 'application/zip' })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${this.detail.report_id}.zip`; anchor.click(); URL.revokeObjectURL(url)
        this.downloadVisible = false; this.downloadReason = ''
      } catch (error) { Message.error(error?.message || '下载诊断包失败') } finally { this.downloading = false }
    }
  }
}
</script>

<style scoped>
.diagnostics-page { padding: 20px; height: calc(100vh - 80px); overflow: auto; background: #f5f7fa; }
.filter-card, .table-card { margin-bottom: 16px; }
.filter-card >>> .el-card__body, .table-card >>> .el-card__body { padding: 16px; }
.pagination { margin-top: 16px; text-align: right; }
.detail-body { max-height: 72vh; overflow: auto; }
.detail-actions { display: flex; gap: 8px; margin: 18px 0 8px; }
.query-text { padding: 10px; border-radius: 4px; background: #f4f4f5; color: #606266; font: 12px/1.5 monospace; white-space: pre-wrap; word-break: break-all; }
.event-meta { margin-left: 8px; color: #909399; font-size: 12px; }
.el-timeline-item p { margin: 5px 0; color: #606266; }
.el-timeline-item code { color: #909399; font-size: 11px; }
</style>
